import { useEffect } from "react";
import {
    Grid,
    Box,
    Typography,
    Autocomplete,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    IconButton
} from "@mui/material";
import moment from "moment";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { esComprobanteBanco } from "../functions";

export default function CabeceraEditar(props) {
    const esBanco = esComprobanteBanco(props.propiedades.comprobanteSelected);

    useEffect(() => {
        if (!props.propiedades.datos.nro_comprobante?.includes("-")) return;

        const [prefijo, nro] = props.propiedades.datos.nro_comprobante.split("-");
        const esTipoBanco =
            esBanco ||
            Number(props.propiedades.datos.id_tipocomprobante) === 10001;

        if (esTipoBanco) {
            props.propiedades.setDatos((prev) => ({
                ...prev,
                id_banco: prev.id_banco ?? Number(parseInt(prefijo, 10)),
                comprobante_nro: nro,
            }));
        } else {
            props.propiedades.setDatos((prev) => ({
                ...prev,
                comprobante_pv: prefijo,
                comprobante_nro: nro,
            }));
        }
    }, [
        props.propiedades.datos.nro_comprobante,
        props.propiedades.comprobanteSelected,
        props.propiedades.datos.id_tipocomprobante,
    ]);

    useEffect(() => {
        // 1. Verificación de seguridad (Si no hay datos, no hacemos nada)
        if (!props.propiedades.datos?.fecha_factura || !props.propiedades.datos?.id_condicionpago) return;

        // 2. Buscamos la condición de pago
        const condicion = props.propiedades.condicionesPago?.find(
            (cond) => Number(cond.id) === Number(props.propiedades.datos.id_condicionpago)
        );

        // Si no encontramos la condición todavía, esperamos al siguiente render
        if (!condicion) return;

        const dias = Number(condicion.dias) || 0;

        // 3. CREAR LA FECHA (Con el fix de las barras para evitar el error de -1 día)
        // Usamos el optional chaining ?. por las dudas
        const fechaStr = props.propiedades.datos.fecha_factura;
        const fechaBase = new Date(fechaStr.replace(/-/g, '\/'));

        // 4. Sumar los días
        fechaBase.setDate(fechaBase.getDate() + dias);

        // 5. Formatear (YYYY-MM-DD)
        const vtoCalculado = fechaBase.toISOString().split("T")[0];

        // 6. Solo actualizamos si el valor cambió para evitar bucles infinitos
        if (props.propiedades.datos.vto_factura !== vtoCalculado) {
            props.propiedades.setDatos((prev) => ({
                ...prev,
                vto_factura: vtoCalculado,
            }));
        }
    }, [
        props.propiedades.datos?.fecha_factura,
        props.propiedades.datos?.id_condicionpago,
        props?.condicionesPago
    ]);

    return (
        <div>
            <Grid container spacing={2}>
                <Grid item xs={11.5} sm={5.5} md={2.5}>
                    <Autocomplete
                        sx={{ backgroundColor: "white" }}
                        disableClearable
                        placeholder="Nombre, CUIT o SisvenID"
                        options={props.propiedades.proveedores || []}
                        value={props.propiedades.proveedorSelected || null}
                        inputValue={props.inputValue}
                        loading={props.propiedades.loading}
                        loadingText={"Cargando"}
                        onChange={(e, value) => {
                            props.handleOnChangeProvSel(e, value);
                        }}
                        isOptionEqualToValue={(option, value) =>
                            (option.cuit || option.cuit_proveedor) === (value.cuit || value.cuit_proveedor)
                        }
                        noOptionsText={"No existe proveedor"}
                        onInputChange={(e, value, reason) => {
                            // 1. Siempre actualizamos el texto que el usuario ve
                            props.setInputValue(value);

                            // 2. Limpiamos el timeout anterior correctamente
                            if (props.timeoutRef.current) {
                                clearTimeout(props.timeoutRef.current); // <-- Corregido: antes decía clearTimeout.current
                            }

                            // 3. SOLO buscamos si el usuario está ESCRIBIENDO (input) 
                            // y si borró o escribió más de 2 caracteres
                            if (reason === "input") {
                                if (value?.length >= 2) {
                                    props.timeoutRef.current = setTimeout(() => {
                                        props.propiedades.getProveedores(
                                            value,
                                            props.propiedades.setLoading,
                                            props.propiedades.setProveedores
                                        );
                                        props.timeoutRef.current = null;
                                    }, 300);
                                } else {
                                    props.propiedades.setProveedores([]);
                                }
                            }
                        }}
                        getOptionLabel={(option) =>
                            `${option?.denominacion} (${option?.cuit})`
                        }
                        renderOption={(props, option) => (
                            <Box
                                component="li"
                                key={option.denominacion}
                                sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                                {...props}>
                                {`${option?.denominacion} (${option?.cuit})`}
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                                focused
                                fullWidth
                                placeholder="Tipee para buscar proveedores..."
                                {...params}
                                variant="outlined"
                                size="small"
                                label="Proveedores"
                                required={props.propiedades.proveedorSelected === null} />
                        )}
                    />
                </Grid>
                <Grid
                    item
                    xs={0.5}
                    sm={0.5}
                    md={0.5}
                    display="flex"
                    alignItems="flex-end"
                    justifyContent="flex-end"
                >
                    <Tooltip title="Nuevo proveedor">
                        <IconButton
                            color="primary"
                            onClick={props.propiedades.handleAddProveedor}
                            size="medium"
                        >
                            <AddCircleOutlineIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>
                <Grid
                    container
                    pl={1}
                    xs={12}
                    sm={esBanco ? 6 : 6}
                    md={esBanco ? 4 : 3}
                    lg={esBanco ? 4 : 3}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-end"
                >
                    {esBanco ? (
                        <Tooltip title="Banco del resumen bancario.">
                            <Grid item xs={8} sm={8} md={8} lg={8} mt={1}>
                                <FormControl style={props.propiedades.style} focused required fullWidth size="small">
                                    <InputLabel>Banco</InputLabel>
                                    <Select
                                        name="id_banco"
                                        value={props.propiedades.datos.id_banco ?? ""}
                                        label="Banco"
                                        onChange={props.almacenarCabecera}
                                    >
                                        {props.propiedades.bancos?.map((banco) => (
                                            <MenuItem key={banco.id} value={banco.id}>
                                                {banco.nombre}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Tooltip>
                    ) : (
                    <Tooltip title="Punto de Venta del comprobante cargado.">
                        <Grid item xs={3.3} sm={3.3} md={3.3} lg={3.3} mt={1}>
                            <TextField
                                label="Pto Venta"
                                name="comprobante_pv"
                                // Usamos el valor tal cual está en el estado
                                value={props.propiedades.datos.comprobante_pv || ""}
                                onChange={props.almacenarCabecera}
                                // IMPORTANTE: Quitamos el onBlur que tenía el padStart
                                inputProps={{ maxLength: 4 }}
                                fullWidth
                                variant="outlined"
                                size="small"
                                style={props.propiedades.style}
                                focused
                                required
                                sx={{ "& .MuiInputBase-input": { textAlign: "right" } }}
                            />

                        </Grid>
                    </Tooltip>
                    )}

                    <Typography
                        variant="body1"
                        sx={{
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            height: "65%",
                        }}
                    >
                        -
                    </Typography>

                    <Tooltip title="N° del comprobante cargado.">
                        <Grid item xs={esBanco ? 3 : 8.3} sm={esBanco ? 3 : 8.3} md={esBanco ? 3 : 8.3} lg={esBanco ? 3 : 8.3}>
                            <TextField
                                label="Nro Comprobante"
                                name="comprobante_nro"
                                value={props.propiedades.datos.comprobante_nro || ""}
                                onChange={props.almacenarCabecera}
                                // Sin onBlur de formateo
                                inputProps={{ maxLength: 8 }}
                                fullWidth
                                style={props.propiedades.style}
                                variant="outlined"
                                size="small"
                                focused
                                required
                            />
                        </Grid>
                    </Tooltip>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <FormControl style={props.propiedades.style} focused required>
                        <InputLabel>Condicion de venta</InputLabel>
                        <Select
                            size="small"
                            name="id_condicionpago"
                            value={props.propiedades.datos.id_condicionpago || ""}
                            label="Condicion de venta"
                            onChange={props.almacenarCabecera}
                        >
                            {props.propiedades.condicionesPago.map((elemento) => (
                                <MenuItem key={elemento.id} value={elemento.id}>
                                    {elemento.condicion}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormControl style={props.propiedades.style} focused required>
                        <InputLabel>Empresa</InputLabel>
                        <Select
                            size="small"
                            name="id_empresa"
                            value={props.propiedades.datos.id_empresa}
                            label="Empresa "
                            onChange={props.almacenarCabecera}
                        >
                            <MenuItem
                                key={2}
                                value={2}
                            >
                                Italgas
                            </MenuItem>
                            <MenuItem
                                key={4}
                                value={4}
                            >
                                Natural Gas
                            </MenuItem>
                            <MenuItem
                                key={5}
                                value={5}
                            >
                                Metalgar
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormControl style={props.propiedades.style} focused required>
                        <InputLabel>Sucursal</InputLabel>
                        <Select
                            size="small"
                            name="id_sucursal"
                            value={props.propiedades.datos.id_sucursal}
                            label="Sucursal "
                            onChange={props.almacenarCabecera}
                        >
                            {props.propiedades.sucursales
                                ? props.propiedades.sucursales.map((elemento) => (
                                    <MenuItem key={elemento.id} value={elemento.id}>
                                        {elemento.nombre}
                                    </MenuItem>
                                ))
                                : null}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                        required
                        sx={{ backgroundColor: "white" }}
                        disableClearable
                        value={props.propiedades.comprobanteSelected || null}
                        placeholder="Tipo Comprobante"
                        options={props.propiedades.tiposComprobantes || []}
                        onChange={(event, newValue) => {
                            props.propiedades.setcomprobanteSelected(newValue);
                            props.propiedades.setDatos({
                                ...props.propiedades.datos,
                                id_tipocomprobante: newValue ? newValue.id_tipo_comprobante : null
                            });
                        }}
                        noOptionsText="Tipee para buscar comprobante..."
                        isOptionEqualToValue={(option, value) => option.id_tipo_comprobante === value.id_tipocomprobante}
                        getOptionLabel={(option) =>
                            `${option?.descripcion} (${option?.id_tipo_comprobante})`
                        }
                        renderOption={(props, option) => (
                            <Box
                                component="li"
                                key={option.id_tipo_comprobante}
                                sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                                {...props}
                            >
                                {option?.descripcion} ({option?.id_tipo_comprobante})
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                                focused
                                fullWidth
                                placeholder="Tipee para buscar tipo de comprobante.."
                                {...params}
                                variant="outlined"
                                size="small"
                                label="Tipo Comprobante"
                            />
                        )}
                    />
                </Grid>

                {esBanco && (
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            onChange={props.almacenarCabecera}
                            fullWidth
                            variant="outlined"
                            size="small"
                            style={props.propiedades.style}
                            focused
                            required
                            value={props.propiedades.datos.concepto ?? ""}
                            name="concepto"
                            label="Concepto"
                        />
                    </Grid>
                )}

                {esBanco ? (
                    <Grid item xs={12} sm={4} md={2}>
                        <TextField
                            type="date"
                            focused
                            fullWidth
                            required
                            size="small"
                            color="primary"
                            style={props.propiedades.style}
                            label="Fecha"
                            name="fecha_factura"
                            value={moment(props.propiedades.datos.fecha_factura).format("YYYY-MM-DD")}
                            onChange={props.almacenarCabecera}
                            disabled
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                ) : (
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        type="date"
                        focused
                        fullWidth
                        required
                        size="small"
                        color="primary"
                        style={props.propiedades.style}
                        label="Fecha de comprobante"
                        name="fecha_factura"
                        value={moment(props.propiedades.datos.fecha_factura).format("YYYY-MM-DD")}
                        onChange={props.almacenarCabecera}
                    // inputProps={{
                    //   max: moment().startOf("month").format("YYYY-MM-DD"),
                    // }}
                    />
                </Grid>
                )}
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        InputProps={{
                            readOnly: true,
                        }}
                        disabled
                        type="date"
                        focused
                        fullWidth
                        size="small"
                        color="primary"
                        style={props.propiedades.style}
                        label="Vencimiento comprobante"
                        name="vto_factura"
                        // 2. Usamos el valor directo del estado. 
                        // Si usás el estado local "datos", poné: datos.vto_factura
                        // Si usás el de props, dejalo como está abajo:
                        value={props.propiedades.datos.vto_factura || ""}

                        // 3. El min es excelente para que no pongan un vencimiento anterior a la factura
                        inputProps={{
                            min: props.propiedades.datos.fecha_factura,
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        disabled={!props.propiedades.proveedorSelected}
                        style={props.propiedades.style}
                        focused

                        type="text"
                        autoComplete="off"
                        fullWidth
                        size="small"
                        label="Importe total Factura"
                        name="totalMuestro"
                        value={props.propiedades.totalMuestro}
                        onKeyPress={(event) => {
                            const { value } = event.target;

                            if (!/[\d,]/.test(event.key)) {
                                event.preventDefault();
                            }

                            if (event.key === "," && value.includes(",")) {
                                event.preventDefault();
                            }
                        }} //coma para los decimales
                        onChange={(e,) => {
                            props.propiedades.handleOnChangeTotal(e, props.propiedades.setTotal, props.propiedades.setTotalMuestro)
                        }}
                    // onBlur={handleOnBlurTotal} //esta comentado porque todavia no vamos a implementar el monto minimo sin orden de compra
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <FormControl style={props.propiedades.style} focused required>
                        <InputLabel>Moneda</InputLabel>
                        <Select
                            label="Moneda"
                            size="small"
                            name="id_tipomoneda"
                            value={
                                props.propiedades.datos.id_tipomoneda ? props.propiedades.datos.id_tipomoneda : " "
                            }
                            onChange={props.almacenarCabecera}
                        >
                            {props.propiedades.tipoMoneda
                                ? props.propiedades.tipoMoneda.map((elemento) => (
                                    <MenuItem key={elemento.id} value={elemento.id}>
                                        {elemento.valor}
                                    </MenuItem>
                                ))
                                : null}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        label="Tipo de cambio"
                        disabled={props.propiedades.datos.id_tipomoneda === 83}
                        name="tipo_cambio"
                        value={props.propiedades.datos.tipo_cambio}
                        // onBlur={handleOnBlurMontoMaximo}

                        onChange={(e) => {
                            let value = e.target.value.replace(",", ".");
                            const regex = /^\d{0,4}(\.\d{0,2})?$/;

                            if (value === "" || regex.test(value)) {
                                props.almacenarCabecera({
                                    target: {
                                        name: e.target.name,
                                        value: value,
                                    },
                                });
                            }
                        }}
                        onBlur={(e) => {
                            let value = e.target.value.replace(",", ".");
                            if (value) {
                                const formatted = parseFloat(value).toFixed(2);
                                props.almacenarCabecera({
                                    ...e,
                                    target: {
                                        ...e.target,
                                        value: formatted,
                                    },
                                });
                            }
                        }}
                        // onBlur={(e) => {
                        //   handleOnBlurMontoMaximo,
                        //   let value = e.target.value.replace(",", ".");
                        //   if (value) {
                        //     const formatted = parseFloat(value).toFixed(2);

                        //     almacenarCabecera({
                        //       target: {
                        //         name: e.target.name,
                        //         value: formatted,
                        //       },
                        //     });
                        //   }
                        // }}

                        onKeyDown={(e) => {
                            const { key } = e;
                            const value = e.target.value;

                            const isNumber = /^[0-9]$/.test(key);
                            const isDotOrComma = key === "." || key === ",";
                            const isControl = [
                                "Backspace",
                                "Tab",
                                "ArrowLeft",
                                "ArrowRight",
                                "Delete",
                            ].includes(key);

                            if (!isNumber && !isDotOrComma && !isControl) {
                                e.preventDefault();
                            }

                            if (
                                isDotOrComma &&
                                (value.includes(".") || value.includes(","))
                            ) {
                                e.preventDefault();
                            }

                            const [intPart] = value.split(/[.,]/);
                            if (
                                isNumber &&
                                intPart.length >= 4 &&
                                !value.includes(".") &&
                                !value.includes(",")
                            ) {
                                e.preventDefault();
                            }
                        }}
                        inputMode="decimal"
                        type="text"
                        fullWidth
                        size="small"
                        focused
                        style={props.propiedades.style}
                        required={props.propiedades.datos.id_tipomoneda !== 83}
                    />
                </Grid>
            </Grid>

        </div>
    )
}